import { create } from 'zustand'
import { api, integrations, subscribeUserEvents, type Health, type Skill, type McpServer } from './lib/api'
import { streamChat, streamResume } from './lib/stream'
import { uid } from './lib/utils'
import type {
  ActivitySegment,
  AppSettings,
  Attachment,
  AuthStatus,
  Chat,
  ChatMode,
  ChatMessage,
  ChatStreamEvent,
  Citation,
  Memory,
  MemoryKind,
  MemoryScope,
  Position,
  Project,
  SessionUser,
  Task,
  TaskInput,
  ToolEvent,
  TradeSide,
  Transaction,
  User,
} from '@shared/types'
import { DEFAULT_MODEL } from '@shared/models'

export type AppView = 'chat' | 'portfolio' | 'settings' | 'tasks' | 'workers'

export type OrbState = 'idle' | 'listening' | 'thinking' | 'searching' | 'coding' | 'speaking'

/* Which orb mood a running tool puts Libera in — research vs computation. */
const ORB_SEARCH_TOOLS = new Set(['web_search', 'x_search', 'search_web', 'web_fetch', 'md_search', 'search_project', 'search_chat'])
const ORB_CODE_TOOLS = new Set(['run_python', 'run_bash', 'data_fetch', 'read_file', 'list_files', 'present_files'])
function orbForTool(name: string): OrbState {
  if (ORB_SEARCH_TOOLS.has(name)) return 'searching'
  if (ORB_CODE_TOOLS.has(name)) return 'coding'
  return 'thinking'
}

export interface PointTarget {
  id: string
  tone: 'up' | 'down' | 'neutral'
  at: number
}

export interface StreamingMsg {
  id: string
  content: string
  reasoning: string
  citations: Citation[]
  toolEvents: ToolEvent[]
  /** Chronological reasoning/tool interleave built live as events arrive. */
  segments: ActivitySegment[]
  /** When this turn started (for the live "Thinking · Ns" ticker). */
  startedAt: number
  /** Frozen ms-to-first-answer-token once text begins. */
  thinkingMs?: number
  /** The server threw away the streamed draft and is writing a corrected answer. */
  revising?: boolean
  /** A notice about the run itself (e.g. it moved from the Grok subscription to the API key). */
  notice?: string
}

function applyTheme(theme: 'dark' | 'light') {
  document.documentElement.setAttribute('data-theme', theme)
}

/* ── URL routing (/c/<chatId>), no router dependency ─────────── */
function chatIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const m = window.location.pathname.match(/^\/c\/([A-Za-z0-9_-]+)$/)
  return m ? m[1] : null
}
/** A compaction's new context reading lands on the newest measured answer (the meter reads it from there). */
function withContext(messages: ChatMessage[], context: NonNullable<ChatMessage['meta']>['context']): ChatMessage[] {
  const i = messages.map((m) => m.role === 'assistant' && !!m.meta?.context).lastIndexOf(true)
  if (i < 0) return messages
  return messages.map((m, j) => (j === i ? { ...m, meta: { ...m.meta, context } } : m))
}

function pushChatUrl(chatId: string | null) {
  if (typeof window === 'undefined') return
  const path = chatId ? `/c/${chatId}` : '/'
  if (window.location.pathname !== path) window.history.pushState({ chatId }, '', path)
}
function replaceChatUrl(chatId: string | null) {
  if (typeof window === 'undefined') return
  window.history.replaceState({ chatId }, '', chatId ? `/c/${chatId}` : '/')
}
let popstateBound = false
let taskPollBound = false
let unauthBound = false

interface AppState {
  ready: boolean
  user: SessionUser | null
  setupRequired: boolean
  health: Health | null
  theme: 'dark' | 'light'
  settings: AppSettings | null

  adminOpen: boolean
  users: User[]

  view: AppView
  positions: Position[]
  transactions: Transaction[]
  tasks: Task[]
  taskUnread: number
  /** The tasks that run inside the open chat (its schedule strip). */
  chatTasks: Task[]
  /** The task editor (a modal over any view): editing `task`, or a new one from `preset`. */
  taskEditor: { task: Task | null; preset?: Partial<TaskInput> } | null

  chats: Chat[]
  projects: Project[]
  activeChatId: string | null
  pendingProjectId: string | null
  messages: ChatMessage[]
  // Request to scroll+flash a specific message (in-chat search / global search jump). `n`
  // is a nonce so repeated jumps to the SAME message still re-trigger the effect.
  scrollTarget: { id: string; n: number } | null

  streaming: boolean
  streamingMsg: StreamingMsg | null
  abort: AbortController | null

  orb: OrbState
  point: PointTarget | null

  memory: Memory[]
  watchlist: string[]

  sidebarOpen: boolean
  calculatorOpen: boolean
  calcSeed: string | null
  memoryOpen: boolean
  symbolModal: string | null
  liveSearch: boolean
  error: string | null
  // Skills / MCP — the library + the CURRENT chat's active selection (per-chat).
  skillsList: Skill[]
  mcpList: McpServer[]
  chatSkillIds: string[]
  chatMcpIds: string[]
  loadIntegrations: () => Promise<void>
  toggleChatSkill: (id: string) => void
  toggleChatMcp: (id: string) => void
  // Composer model mode (fast/expert/heavy) for the current chat; changeable mid-chat.
  chatMode: ChatMode
  setChatMode: (m: ChatMode) => void
  // Per-chat MODEL (grok-4.3 / grok-4.6 — shared/models.ts registry); changeable mid-chat.
  chatModel: string
  setChatModel: (m: string) => void
  /** Per-chat Trading switch (admin): the trade-worker tools ride this chat's turns. */
  chatTrading: boolean
  setChatTrading: (on: boolean) => void

  init: () => Promise<void>
  loadUserData: () => Promise<void>
  login: (username: string, password: string) => Promise<void>
  setupAdmin: (username: string, password: string, name?: string) => Promise<void>
  logout: () => Promise<void>
  toggleAdmin: (open?: boolean) => void
  loadUsers: () => Promise<void>
  adminCreateUser: (b: { username: string; password: string; name?: string; role?: string }) => Promise<User>
  adminPatchUser: (
    id: string,
    patch: { tokenLimit?: number | null; taskDailyLimit?: number | null; taskOccasionalLimit?: number | null; maxFiles?: number | null },
  ) => Promise<void>
  adminDeleteUser: (id: string) => Promise<void>
  setTheme: (t: 'dark' | 'light') => void
  setView: (v: AppView) => void
  settingsFocus: 'skills' | 'mcp' | 'devices' | 'broker' | 'robinhood' | 'trading' | 'automations' | 'context' | null
  focusSettings: (s: 'skills' | 'mcp' | 'devices' | 'broker' | 'robinhood' | 'trading' | 'automations' | 'context') => void
  /** Text for the chat composer to take over (e.g. a worker card's Edit button); nonce re-triggers. */
  composerPrefill: { text: string; nonce: number } | null
  prefillComposer: (text: string) => void
  /** Worker selected on the Workers page (drill-in). */
  workerFocus: string | null
  openWorker: (id: string | null) => void
  loadTasks: () => Promise<void>
  loadChatTasks: (chatId: string | null) => Promise<void>
  openTaskEditor: (o: { task?: Task | null; preset?: Partial<TaskInput> } | null) => void
  refreshTaskUnread: () => Promise<void>
  saveTask: (input: TaskInput, id?: string) => Promise<Task | null>
  removeTask: (id: string) => Promise<void>
  toggleTask: (id: string, enabled: boolean) => Promise<void>
  runTaskNow: (id: string) => Promise<void>
  updateSettings: (patch: Partial<AppSettings>) => Promise<void>
  loadPortfolio: () => Promise<void>
  addToWatchlist: (symbol: string) => Promise<void>
  removeFromWatchlist: (symbol: string) => Promise<void>
  addPosition: (symbol: string, quantity: number, costBasis: number, stopLoss?: number | null, takeProfit?: number | null) => Promise<void>
  portfolioRemove: (symbol: string) => Promise<void>
  setLevels: (symbol: string, stopLoss: number | null, takeProfit: number | null) => Promise<void>
  trade: (b: { symbol: string; side: TradeSide; quantity: number; price: number; fee?: number; note?: string; stopLoss?: number | null; takeProfit?: number | null }) => Promise<void>
  toggleSidebar: (open?: boolean) => void
  toggleCalculator: (open?: boolean) => void
  pushToCalculator: (expr: string) => void
  openSymbol: (symbol: string) => void
  closeSymbol: () => void
  toggleLiveSearch: () => void
  setOrb: (s: OrbState) => void
  pointAt: (id: string, tone: 'up' | 'down' | 'neutral') => void
  clearPoint: () => void

  toggleMemory: (open?: boolean) => void
  upsertMemory: (
    scope: MemoryScope,
    scopeId: string | null,
    kind: MemoryKind,
    label: string,
    value: unknown,
  ) => Promise<void>
  removeMemory: (id: string) => Promise<void>

  refreshChats: () => Promise<void>
  refreshMemory: () => Promise<void>
  refreshProjects: () => Promise<void>
  newChat: (projectId?: string | null) => void
  selectChat: (id: string, push?: boolean) => Promise<void>
  jumpToMessage: (chatId: string, messageId: string) => Promise<void>
  deleteChat: (id: string) => Promise<void>
  renameChat: (id: string, title: string) => Promise<void>
  togglePin: (chat: Chat) => Promise<void>

  createProject: (name?: string) => Promise<Project | null>
  renameProject: (id: string, name: string) => Promise<void>
  deleteProject: (id: string) => Promise<void>
  assignChat: (chatId: string, projectId: string | null) => Promise<void>

  send: (text: string, attachments?: Attachment[]) => Promise<void>
  compactChat: (focus?: string) => Promise<void>
  stop: () => void
  /** Stop listening without stopping the answer — the server finishes and saves it. */
  detach: () => void
  /** Re-attach to an answer still being generated server-side for this chat (after reload/reconnect). */
  resumeChat: (chatId: string) => Promise<void>
}

type StoreSet = (partial: (s: AppState) => Partial<AppState>) => void

/**
 * The single stream-event reducer, shared by a fresh send() and a resume() re-attach so
 * both render identically. `markFinalized` fires when the persisted 'message' arrives.
 */
function buildStreamHandler(set: StoreSet, chatId: string, markFinalized: () => void) {
  return (e: ChatStreamEvent) => {
    set((s) => {
      const sm = s.streamingMsg
      switch (e.type) {
        case 'start':
          return sm ? { streamingMsg: { ...sm, id: e.messageId } } : {}
        case 'text':
          if (!sm) return {}
          return {
            streamingMsg: { ...sm, content: sm.content + e.delta, thinkingMs: sm.thinkingMs ?? Date.now() - sm.startedAt, revising: false },
            orb: 'speaking',
          }
        case 'text_reset':
          return sm ? { streamingMsg: { ...sm, content: '', revising: e.reason === 'revise' }, orb: 'thinking' } : {}
        case 'reasoning': {
          if (!sm) return { orb: 'thinking' }
          const segs = sm.segments.slice()
          const last = segs[segs.length - 1]
          if (last && last.kind === 'reasoning') segs[segs.length - 1] = { kind: 'reasoning', text: last.text + e.delta }
          else segs.push({ kind: 'reasoning', text: e.delta })
          return { streamingMsg: { ...sm, reasoning: sm.reasoning + e.delta, segments: segs }, orb: 'thinking' }
        }
        case 'tool': {
          if (!sm) return {}
          const evs = sm.toolEvents.slice()
          const i = evs.findIndex((t) => t.id === e.event.id)
          const isNew = i < 0
          if (i >= 0) evs[i] = e.event
          else evs.push(e.event)
          const segs = isNew ? [...sm.segments, { kind: 'tool' as const, eventId: e.event.id }] : sm.segments
          // Libera's mood follows the work: searching / coding while a tool runs.
          const orb: OrbState = e.event.status === 'running' ? orbForTool(e.event.name) : s.orb
          return { streamingMsg: { ...sm, toolEvents: evs, segments: segs }, orb }
        }
        case 'citations': {
          if (!sm) return {}
          const urls = new Set(sm.citations.map((c) => c.url))
          const merged = [...sm.citations, ...e.citations.filter((c) => !urls.has(c.url))]
          return { streamingMsg: { ...sm, citations: merged } }
        }
        case 'title':
          return { chats: s.chats.map((c) => (c.id === chatId ? { ...c, title: e.title } : c)) }
        case 'message':
          markFinalized()
          return { messages: [...s.messages, e.message], streamingMsg: null }
        case 'compacted':
          return {
            chats: s.chats.map((c) => (c.id === chatId ? { ...c, compactedAt: e.compactedAt } : c)),
            ...(e.context ? { messages: withContext(s.messages, e.context) } : {}),
          }
        case 'notice':
          return sm ? { streamingMsg: { ...sm, notice: e.text } } : {}
        case 'error':
          return { error: e.message }
        default:
          return {}
      }
    })
  }
}

export const useStore = create<AppState>((set, get) => ({
  ready: false,
  user: null,
  setupRequired: false,
  health: null,
  theme: 'dark',
  settings: null,
  adminOpen: false,
  users: [],
  view: 'chat',
  positions: [],
  transactions: [],
  tasks: [],
  taskUnread: 0,
  chatTasks: [],
  taskEditor: null,
  chats: [],
  projects: [],
  activeChatId: null,
  pendingProjectId: null,
  messages: [],
  scrollTarget: null,
  streaming: false,
  streamingMsg: null,
  abort: null,
  orb: 'idle',
  point: null,
  memory: [],
  watchlist: [],
  sidebarOpen: true,
  calculatorOpen: false,
  calcSeed: null,
  memoryOpen: false,
  symbolModal: null,
  liveSearch: true,
  skillsList: [],
  mcpList: [],
  chatSkillIds: [],
  chatMcpIds: [],
  chatMode: 'fast',
  chatModel: DEFAULT_MODEL,
  chatTrading: false,
  error: null,

  async init() {
    if (typeof window !== 'undefined' && !unauthBound) {
      unauthBound = true
      window.addEventListener('lb-unauthorized', () => {
        if (get().user) set({ user: null, error: 'Session expired — please sign in again.' })
      })
    }
    applyTheme(get().theme)
    try {
      const [status, health] = await Promise.all([
        api.authStatus().catch(() => ({ setupRequired: false, user: null }) as AuthStatus),
        api.health().catch(() => null),
      ])
      set({ health, user: status.user, setupRequired: status.setupRequired })
      if (status.user) await get().loadUserData()
      set({ ready: true })
    } catch (err) {
      set({ ready: true, error: err instanceof Error ? err.message : String(err) })
    }
  },

  async loadUserData() {
    const [settings, chats, projects, memory, pf] = await Promise.all([
      api.getSettings().catch(() => null),
      api.listChats().catch(() => [] as Chat[]),
      api.listProjects().catch(() => [] as Project[]),
      api.listMemory().catch(() => [] as Memory[]),
      api.getPortfolio().catch(() => ({ positions: [] as Position[], transactions: [] as Transaction[], watchlist: [] as string[] })),
    ])
    const theme = settings?.theme ?? get().theme
    applyTheme(theme)
    set({
      settings,
      theme,
      chats,
      projects,
      memory,
      positions: pf.positions,
      transactions: pf.transactions,
      watchlist: pf.watchlist ?? [],
      liveSearch: settings?.liveSearch ?? true,
      sidebarOpen: typeof window !== 'undefined' ? window.innerWidth >= 768 : true,
    })
    void get().refreshTaskUnread()
    void get().loadIntegrations()
    if (typeof window !== 'undefined' && !taskPollBound) {
      taskPollBound = true
      setInterval(() => {
        if (document.visibilityState === 'visible') void get().refreshTaskUnread()
      }, 60_000)
      // Server pushes: an automation woke the AI in a chat (follow it live if that chat is open), tasks changed.
      subscribeUserEvents((e) => {
        const s = get()
        if (e.type === 'wake') {
          if (e.phase === 'done') {
            void s.refreshChats()
            void s.refreshTaskUnread()
          }
          if (e.chatId !== s.activeChatId || s.view !== 'chat' || s.streaming) return
          void api
            .getChat(e.chatId)
            .then(({ messages }) => {
              if (get().activeChatId !== e.chatId || get().streaming) return
              set({ messages })
              if (e.phase === 'start') void get().resumeChat(e.chatId)
            })
            .catch(() => {})
        } else if (e.type === 'tasks') {
          void s.refreshTaskUnread()
          void s.loadChatTasks(s.activeChatId)
          if (s.view === 'tasks' || s.tasks.length) void s.loadTasks()
        }
      })
    }
    const urlId = chatIdFromUrl()
    // /w/<workerId> (Telegram's "Düzenle" link) opens that worker in the Workers view.
    const workerLink = typeof window !== 'undefined' ? window.location.pathname.match(/^\/w\/(w_[A-Za-z0-9_-]+)$/) : null
    if (workerLink) {
      replaceChatUrl(null)
      get().openWorker(workerLink[1])
    } else if (urlId && chats.some((c) => c.id === urlId)) await get().selectChat(urlId, false)
    else if (urlId) replaceChatUrl(null)
    if (typeof window !== 'undefined' && !popstateBound) {
      popstateBound = true
      window.addEventListener('popstate', () => {
        const id = chatIdFromUrl()
        if (id) void get().selectChat(id, false)
        else get().newChat()
      })
    }
  },

  async login(username, password) {
    const { user } = await api.login({ username, password })
    set({ user, setupRequired: false, error: null })
    await get().loadUserData()
  },
  async setupAdmin(username, password, name) {
    const { user } = await api.setup({ username, password, name })
    set({ user, setupRequired: false, error: null })
    await get().loadUserData()
  },
  async logout() {
    if (get().streaming) get().detach()
    await api.logout().catch(() => {})
    set({
      user: null,
      chats: [],
      projects: [],
      messages: [],
      activeChatId: null,
      streamingMsg: null,
      memory: [],
      users: [],
      adminOpen: false,
      memoryOpen: false,
      calculatorOpen: false,
    })
    replaceChatUrl(null)
  },
  toggleAdmin(open) {
    set((s) => ({ adminOpen: open ?? !s.adminOpen }))
    if (open !== false) void get().loadUsers()
  },
  async loadUsers() {
    const users = await api.listUsers().catch(() => [] as User[])
    set({ users })
  },
  async adminCreateUser(b) {
    const u = await api.createUser(b)
    set((s) => ({ users: [...s.users, u] }))
    return u
  },
  async adminPatchUser(id, patch) {
    try {
      const u = await api.patchUser(id, patch)
      set((s) => ({ users: s.users.map((x) => (x.id === id ? u : x)) }))
    } catch {
      /* ignore */
    }
  },
  async adminDeleteUser(id) {
    await api.deleteUser(id).catch(() => {})
    set((s) => ({ users: s.users.filter((u) => u.id !== id) }))
  },

  setTheme(theme) {
    applyTheme(theme)
    set({ theme })
    api.saveSettings({ theme }).catch(() => {})
  },
  setView(view) {
    set({ view })
    if (view !== 'settings') set({ settingsFocus: null })
    if (typeof window !== 'undefined' && window.innerWidth < 768) set({ sidebarOpen: false })
    if (view === 'tasks') {
      void get().loadTasks()
      // Opening Tasks clears the unread badge.
      api.tasksMarkRead().then(() => set({ taskUnread: 0 })).catch(() => {})
    }
  },
  composerPrefill: null,
  prefillComposer(text) {
    set({ composerPrefill: { text, nonce: Date.now() } })
  },
  workerFocus: null,
  openWorker(id) {
    set({ view: 'workers', workerFocus: id })
  },
  settingsFocus: null,
  focusSettings(s) {
    set({ view: 'settings', settingsFocus: s })
    if (typeof window !== 'undefined' && window.innerWidth < 768) set({ sidebarOpen: false })
  },
  async loadTasks() {
    const tasks = await api.tasks().catch(() => [])
    set({ tasks })
  },
  async loadChatTasks(chatId) {
    if (!chatId) return set({ chatTasks: [] })
    const chatTasks = await api.chatTasks(chatId).catch(() => [])
    if (get().activeChatId === chatId) set({ chatTasks })
  },
  openTaskEditor(o) {
    set({ taskEditor: o ? { task: o.task ?? null, preset: o.preset } : null })
  },
  async refreshTaskUnread() {
    const r = await api.taskUnread().catch(() => ({ count: 0 }))
    set({ taskUnread: r.count })
  },
  async saveTask(input, id) {
    const task = id ? await api.updateTask(id, input) : await api.createTask(input)
    set((s) => ({
      tasks: id ? s.tasks.map((t) => (t.id === id ? task : t)) : [task, ...s.tasks],
    }))
    void get().loadChatTasks(get().activeChatId)
    return task
  },
  async removeTask(id) {
    await api.deleteTask(id).catch(() => {})
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id), chatTasks: s.chatTasks.filter((t) => t.id !== id) }))
  },
  async toggleTask(id, enabled) {
    const patch = (t: Task) => (t.id === id ? { ...t, enabled } : t)
    set((s) => ({ tasks: s.tasks.map(patch), chatTasks: s.chatTasks.map(patch) }))
    const task = await api.updateTask(id, { enabled }).catch(() => null)
    if (task) set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? task : t)), chatTasks: s.chatTasks.map((t) => (t.id === id ? task : t)) }))
  },
  async runTaskNow(id) {
    const run = await api.runTask(id).catch((e: Error) => {
      set({ error: e.message })
      return null
    })
    if (!run) return
    // A task in a chat runs there: open it and follow the run live.
    if ('started' in run) {
      if (get().activeChatId !== run.chatId || get().view !== 'chat') await get().selectChat(run.chatId)
      return
    }
    set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? { ...t, lastRunAt: run.createdAt } : t)) }))
    get().refreshChats()
  },
  async updateSettings(patch) {
    set((s) => ({ settings: s.settings ? { ...s.settings, ...patch } : s.settings }))
    if (patch.theme) {
      set({ theme: patch.theme })
      applyTheme(patch.theme)
    }
    if (patch.liveSearch !== undefined) set({ liveSearch: patch.liveSearch })
    const next = await api.saveSettings(patch).catch(() => null)
    if (next) set({ settings: next })
  },
  async loadPortfolio() {
    const data = await api.getPortfolio().catch(() => ({ positions: [], transactions: [], watchlist: [] as string[] }))
    set({ positions: data.positions, transactions: data.transactions, watchlist: data.watchlist ?? [] })
  },
  async addToWatchlist(symbol) {
    const res = await api.addWatchlist(symbol).catch(() => null)
    if (res) set({ watchlist: res.watchlist })
  },
  async removeFromWatchlist(symbol) {
    const res = await api.removeWatchlist(symbol).catch(() => null)
    if (res) set({ watchlist: res.watchlist })
  },
  async addPosition(symbol, quantity, costBasis, stopLoss = null, takeProfit = null) {
    const res = await api.setPosition({ symbol, quantity, costBasis, stopLoss, takeProfit }).catch(() => null)
    if (res) set({ positions: res.positions })
  },
  async portfolioRemove(symbol) {
    const res = await api.removePosition(symbol).catch(() => null)
    if (res) set({ positions: res.positions })
  },
  async setLevels(symbol, stopLoss, takeProfit) {
    const res = await api.setLevels({ symbol, stopLoss, takeProfit }).catch(() => null)
    if (res) set({ positions: res.positions })
  },
  async trade(b) {
    const res = await api.trade(b).catch(() => null)
    if (res) set((s) => ({ positions: res.positions, transactions: [res.transaction, ...s.transactions] }))
  },

  toggleSidebar(open) {
    set((s) => ({ sidebarOpen: open ?? !s.sidebarOpen }))
  },
  toggleCalculator(open) {
    set((s) => ({ calculatorOpen: open ?? !s.calculatorOpen }))
  },
  pushToCalculator(expr) {
    set({ calculatorOpen: true, calcSeed: expr })
  },
  openSymbol(symbol) {
    set({ symbolModal: symbol.trim().toUpperCase() })
  },
  closeSymbol() {
    set({ symbolModal: null })
  },
  toggleLiveSearch() {
    set((s) => {
      const liveSearch = !s.liveSearch
      api.saveSettings({ liveSearch }).catch(() => {})
      return { liveSearch }
    })
  },

  async loadIntegrations() {
    const [skillsList, mcpList] = await Promise.all([
      integrations.skills().catch(() => [] as Skill[]),
      integrations.mcp().catch(() => [] as McpServer[]),
    ])
    set((s) => {
      // If the current chat has no explicit selection yet, default to the on-by-default set.
      const ch = s.chats.find((c) => c.id === s.activeChatId)
      const chatSkillIds = ch?.skillIds ?? (s.chatSkillIds.length ? s.chatSkillIds : skillsList.filter((x) => x.enabled).map((x) => x.id))
      const chatMcpIds = ch?.mcpIds ?? (s.chatMcpIds.length ? s.chatMcpIds : mcpList.filter((x) => x.enabled).map((x) => x.id))
      const chatMode = ch?.mode ?? s.chatMode
      const chatModel = ch?.model ?? s.chatModel
      return { skillsList, mcpList, chatSkillIds, chatMcpIds, chatMode, chatModel }
    })
  },
  toggleChatSkill(id) {
    set((s) => {
      const has = s.chatSkillIds.includes(id)
      const chatSkillIds = has ? s.chatSkillIds.filter((x) => x !== id) : [...s.chatSkillIds, id]
      if (s.activeChatId) api.setChatTools(s.activeChatId, chatSkillIds, s.chatMcpIds).catch(() => {})
      return { chatSkillIds }
    })
  },
  toggleChatMcp(id) {
    set((s) => {
      const has = s.chatMcpIds.includes(id)
      const chatMcpIds = has ? s.chatMcpIds.filter((x) => x !== id) : [...s.chatMcpIds, id]
      if (s.activeChatId) api.setChatTools(s.activeChatId, s.chatSkillIds, chatMcpIds).catch(() => {})
      return { chatMcpIds }
    })
  },
  setChatMode(mode) {
    set((s) => {
      // Persist on an existing chat; for a not-yet-created chat it rides the next send().
      if (s.activeChatId) api.setChatMode(s.activeChatId, mode).catch(() => {})
      return { chatMode: mode }
    })
  },
  setChatModel(model) {
    set((s) => {
      if (s.activeChatId) api.setChatModel(s.activeChatId, model).catch(() => {})
      return { chatModel: model }
    })
  },
  setChatTrading(on) {
    set((s) => {
      // Persist on an existing chat; a not-yet-created chat carries it on the first send().
      if (s.activeChatId) api.setChatTrading(s.activeChatId, on).catch(() => {})
      return { chatTrading: on }
    })
  },

  setOrb(orb) {
    set({ orb })
  },
  pointAt(id, tone) {
    set({ point: { id, tone, at: Date.now() } })
  },
  clearPoint() {
    set({ point: null })
  },

  toggleMemory(open) {
    set((s) => ({ memoryOpen: open ?? !s.memoryOpen }))
  },
  async upsertMemory(scope, scopeId, kind, label, value) {
    await api.saveMemory({ scope, scopeId, kind, label, value }).catch(() => {})
    if (scope === 'global') await get().refreshMemory()
  },
  async removeMemory(id) {
    await api.deleteMemory(id).catch(() => {})
    await get().refreshMemory()
  },

  async refreshChats() {
    const chats = await api.listChats().catch(() => get().chats)
    set({ chats })
  },
  async refreshMemory() {
    const memory = await api.listMemory().catch(() => get().memory)
    set({ memory })
  },
  async refreshProjects() {
    const projects = await api.listProjects().catch(() => get().projects)
    set({ projects })
  },

  newChat(projectId = null) {
    if (get().streaming) get().detach()
    const s = get()
    set({
      activeChatId: null, messages: [], streamingMsg: null, point: null, pendingProjectId: projectId, view: 'chat', chatTasks: [],
      // Fresh chat → start from the on-by-default Skills/MCP; Trading always starts off.
      chatSkillIds: s.skillsList.filter((x) => x.enabled).map((x) => x.id),
      chatMcpIds: s.mcpList.filter((x) => x.enabled).map((x) => x.id),
      chatTrading: false,
    })
    pushChatUrl(null)
    if (typeof window !== 'undefined' && window.innerWidth < 768) set({ sidebarOpen: false })
  },

  async selectChat(id, push = true) {
    // Leaving a chat mid-answer must not cancel it: the answer keeps generating on the server,
    // is saved there, and re-attaches when the chat is opened again.
    if (get().streaming) get().detach()
    set({ activeChatId: id, messages: [], point: null, streamingMsg: null, pendingProjectId: null, view: 'chat', chatTasks: [] })
    if (push) pushChatUrl(id)
    void get().loadChatTasks(id)
    if (typeof window !== 'undefined' && window.innerWidth < 768) set({ sidebarOpen: false })
    try {
      const { chat, messages } = await api.getChat(id)
      if (get().activeChatId === id) {
        const s = get()
        set({
          messages,
          chatSkillIds: chat.skillIds ?? s.skillsList.filter((x) => x.enabled).map((x) => x.id),
          chatMcpIds: chat.mcpIds ?? s.mcpList.filter((x) => x.enabled).map((x) => x.id),
          chatMode: chat.mode ?? 'fast',
          chatModel: chat.model ?? DEFAULT_MODEL,
          chatTrading: !!chat.trading,
        })
        // If an answer is still being generated server-side (we left mid-stream), re-attach.
        void get().resumeChat(id)
      }
    } catch {
      /* chat may have been deleted */
    }
  },
  async jumpToMessage(chatId, messageId) {
    // Open the right chat first (loads its messages), then request the scroll+flash.
    if (get().activeChatId !== chatId) await get().selectChat(chatId)
    else if (get().view !== 'chat') set({ view: 'chat' })
    // Only aim at the message if we actually landed on the intended chat (selectChat can fail
    // for a deleted chat); MessageList retries while messages render and clears when done.
    if (get().activeChatId === chatId) set((s) => ({ scrollTarget: { id: messageId, n: (s.scrollTarget?.n ?? 0) + 1 } }))
  },

  async deleteChat(id) {
    await api.deleteChat(id).catch(() => {})
    const remaining = get().chats.filter((c) => c.id !== id)
    set({ chats: remaining })
    if (get().activeChatId === id) {
      if (remaining.length) await get().selectChat(remaining[0].id)
      else get().newChat()
    }
  },

  async renameChat(id, title) {
    set((s) => ({ chats: s.chats.map((c) => (c.id === id ? { ...c, title } : c)) }))
    await api.patchChat(id, { title }).catch(() => {})
  },

  async togglePin(chat) {
    const pinned = !chat.pinned
    set((s) => ({ chats: s.chats.map((c) => (c.id === chat.id ? { ...c, pinned } : c)) }))
    await api.patchChat(chat.id, { pinned }).catch(() => {})
    await get().refreshChats()
  },

  async createProject(name) {
    try {
      const p = await api.createProject(name)
      set((s) => ({ projects: [p, ...s.projects] }))
      return p
    } catch {
      return null
    }
  },
  async renameProject(id, name) {
    set((s) => ({ projects: s.projects.map((p) => (p.id === id ? { ...p, name } : p)) }))
    await api.patchProject(id, { name }).catch(() => {})
  },
  async deleteProject(id) {
    await api.deleteProject(id).catch(() => {})
    set((s) => ({ projects: s.projects.filter((p) => p.id !== id) }))
    await get().refreshChats()
  },
  async assignChat(chatId, projectId) {
    set((s) => ({ chats: s.chats.map((c) => (c.id === chatId ? { ...c, projectId } : c)) }))
    await api.patchChat(chatId, { projectId }).catch(() => {})
    await get().refreshChats()
  },

  async compactChat(focus) {
    const id = get().activeChatId
    if (!id || get().streaming) return
    try {
      const r = await api.compactChat(id, focus)
      set((s) => ({
        chats: s.chats.map((c) => (c.id === id ? { ...c, compactedAt: r.compactedAt } : c)),
        ...(r.context ? { messages: withContext(s.messages, r.context) } : {}),
      }))
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Could not compact the conversation' })
    }
  },

  async send(text, attachments) {
    const trimmed = text.trim()
    const atts = attachments ?? []
    if ((!trimmed && atts.length === 0) || get().streaming) return

    // `/compact [focus]` — summarize older messages instead of sending to the AI.
    if (trimmed === '/compact' || trimmed.startsWith('/compact ')) {
      await get().compactChat(trimmed.slice('/compact'.length).trim() || undefined)
      return
    }

    // Ensure a chat exists so it appears in the sidebar immediately.
    let chatId = get().activeChatId
    if (!chatId) {
      try {
        const chat = await api.createChat(undefined, get().pendingProjectId)
        chatId = chat.id
        set((s) => ({ chats: [chat, ...s.chats], activeChatId: chat.id, pendingProjectId: null }))
        replaceChatUrl(chat.id)
      } catch (err) {
        set({ error: err instanceof Error ? err.message : 'Could not create chat' })
        return
      }
    }

    const note = atts.length
      ? `${trimmed ? '\n\n' : ''}${atts.map((a) => `📎 ${a.name}`).join('  ')}`
      : ''
    const userMsg: ChatMessage = {
      id: uid(),
      chatId,
      role: 'user',
      content: trimmed + note,
      createdAt: Date.now(),
    }
    const abort = new AbortController()
    set((s) => ({
      messages: [...s.messages, userMsg],
      streaming: true,
      orb: 'thinking',
      abort,
      error: null,
      streamingMsg: { id: uid(), content: '', reasoning: '', citations: [], toolEvents: [], segments: [], startedAt: Date.now() },
    }))

    let finalized = false
    const isFresh = get().messages.filter((m) => m.role === 'user').length === 1

    await streamChat(
      { chatId, message: trimmed, attachments: atts, liveSearch: get().liveSearch, skillIds: get().chatSkillIds, mcpIds: get().chatMcpIds, mode: get().chatMode, model: get().chatModel, trading: get().chatTrading },
      buildStreamHandler(set, chatId, () => {
        finalized = true
      }),
      abort.signal,
    )

    // finalize
    set((s) => {
      const out: Partial<AppState> = { streaming: false, abort: null, orb: 'idle' }
      if (!finalized && s.streamingMsg) {
        const sm = s.streamingMsg
        const content = sm.content || (s.error ? `⚠️ ${s.error}` : '')
        if (content || sm.toolEvents.length) {
          const msg: ChatMessage = {
            id: sm.id,
            chatId: chatId!,
            role: 'assistant',
            content,
            citations: sm.citations.length ? sm.citations : undefined,
            toolEvents: sm.toolEvents.length ? sm.toolEvents : undefined,
            segments: sm.segments.length ? sm.segments : undefined,
            meta: {
              thinkingMs: sm.thinkingMs ?? Date.now() - sm.startedAt,
              ...(sm.reasoning.trim() ? { reasoning: sm.reasoning } : {}),
            },
            createdAt: Date.now(),
          }
          out.messages = [...s.messages, msg]
        }
      }
      out.streamingMsg = null
      return out
    })

    get().refreshChats()
    if (isFresh) get().refreshMemory()
  },

  stop() {
    const chatId = get().activeChatId
    get().abort?.abort()
    set({ streaming: false, abort: null, orb: 'idle' })
    // Disconnecting no longer stops the server (it keeps generating) — so explicitly tell it to.
    // Only the Stop button does this; navigating away only detaches.
    if (chatId) void fetch(`/api/chat/${chatId}/stop`, { method: 'POST' }).catch(() => {})
  },

  detach() {
    get().abort?.abort()
    set({ streaming: false, abort: null, orb: 'idle' })
  },

  async resumeChat(chatId) {
    // Only when idle and viewing this chat, and only if an assistant reply is PENDING
    // (the last message is the user's) — that means the answer is generating server-side
    // (or already finished + persisted but our local state missed it after a tab switch).
    if (get().streaming || get().activeChatId !== chatId) return
    const msgs = get().messages
    if (!msgs.length || msgs[msgs.length - 1].role !== 'user') return

    // A reply is pending → show the "thinking" state immediately and re-attach to the
    // live generation (replays everything so far, then streams the rest).
    const abort = new AbortController()
    let finalized = false
    set({
      streaming: true,
      orb: 'thinking',
      abort,
      error: null,
      streamingMsg: { id: uid(), content: '', reasoning: '', citations: [], toolEvents: [], segments: [], startedAt: Date.now() },
    })
    const handle = buildStreamHandler(set, chatId, () => {
      finalized = true
    })
    await streamResume(
      chatId,
      (e) => {
        if (get().activeChatId === chatId) handle(e)
      },
      abort.signal,
    )

    // Drop the placeholder (keep partial content if any streamed before the connection ended).
    set((s) => {
      const out: Partial<AppState> = { streaming: false, abort: null, orb: 'idle', streamingMsg: null }
      if (!finalized && s.streamingMsg && (s.streamingMsg.content || s.streamingMsg.toolEvents.length)) {
        const sm = s.streamingMsg
        out.messages = [
          ...s.messages,
          {
            id: sm.id,
            chatId,
            role: 'assistant',
            content: sm.content,
            citations: sm.citations.length ? sm.citations : undefined,
            toolEvents: sm.toolEvents.length ? sm.toolEvents : undefined,
            segments: sm.segments.length ? sm.segments : undefined,
            meta: { thinkingMs: sm.thinkingMs ?? Date.now() - sm.startedAt, ...(sm.reasoning.trim() ? { reasoning: sm.reasoning } : {}) },
            createdAt: Date.now(),
          },
        ]
      }
      return out
    })

    // If we STILL have no reply (the generation finished and dropped from the live buffer
    // before we reconnected), it's persisted on the server — refetch to pick it up.
    if (!finalized && get().activeChatId === chatId) {
      const cur = get().messages
      if (cur.length && cur[cur.length - 1].role === 'user') {
        try {
          const { messages } = await api.getChat(chatId)
          if (get().activeChatId === chatId && messages.length) set({ messages })
        } catch {
          /* ignore — best effort */
        }
      }
    }
    get().refreshChats()
  },
}))

// Recover an in-flight answer when the user returns to a backgrounded or reloaded tab:
// the generation keeps running server-side, so re-attach (or refetch) if a reply is pending.
if (typeof window !== 'undefined') {
  const recover = () => {
    if (document.visibilityState !== 'visible') return
    const s = useStore.getState()
    if (s.activeChatId && !s.streaming) void s.resumeChat(s.activeChatId)
  }
  window.addEventListener('visibilitychange', recover)
  window.addEventListener('focus', recover)
}
